/**
 * PTEL Andalucía - EncodingCorrector
 * 
 * Servicio de corrección UTF-8 con sistema de tiers en cascada.
 * Implementa early-exit para documentos limpios y corrección progresiva.
 * 
 * Pipeline:
 * 1. Early-exit si texto limpio (isSuspicious = false)
 * 2. PRE-NORMALIZACIÓN de caracteres variantes (espacios, comillas, etc.)
 * 3. Intenta doble encoding primero
 * 4. Aplica Tier 1 (Hot) - 80% de casos
 * 5. Si persiste mojibake → Tier 2 (Warm)
 * 6. Si aún persiste → Tier 3 (Cold)
 * 7. Normaliza con NFC
 * 
 * @version 1.1.0 - Añadida pre-normalización de caracteres variantes
 * @date Diciembre 2025
 */

import {
  TIER1_HOT_COMBINED,
  TIER2_WARM_COMBINED,
  TIER3_COLD_COMBINED,
  MOJIBAKE_INDICATORS,
  type MojibakePattern,
} from './mojibakePatterns';
import { isSuspicious, isCleanASCII } from './encodingDetector';
import { normalizeVariantChars } from './charNormalizer';

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

export interface CorrectionResult {
  /** Texto corregido (o original si no hubo cambios) */
  corrected: string;
  /** Si se modificó el texto */
  wasModified: boolean;
  /** Tiers aplicados (vacío si early-exit) */
  tiersApplied: ('CHAR_NORMALIZE' | 'TIER1' | 'TIER2' | 'TIER3' | 'DOUBLE_ENCODING' | 'TRIPLE_ENCODING' | 'NFC')[];
  /** Número de patrones que coincidieron */
  patternsMatched: number;
  /** Razón de early-exit si aplica */
  earlyExitReason?: 'CLEAN_ASCII' | 'NO_MOJIBAKE' | 'EMPTY';
  /** Duración en ms */
  durationMs: number;
}

export interface CorrectionStats {
  totalProcessed: number;
  earlyExits: number;
  tier1Only: number;
  tier2Required: number;
  tier3Required: number;
  doubleEncodingFixed: number;
  tripleEncodingFixed: number;
  averageDurationMs: number;
}

// ============================================================================
// CLASE PRINCIPAL
// ============================================================================

export class EncodingCorrector {
  /** Patrones Tier 1 ordenados por longitud */
  private tier1Patterns: MojibakePattern[];
  /** Patrones Tier 2 ordenados por longitud */
  private tier2Patterns: MojibakePattern[];
  /** Patrones Tier 3 ordenados por longitud */
  private tier3Patterns: MojibakePattern[];
  /** Estadísticas de uso */
  private stats: CorrectionStats;

  /**
   * Mapa inverso Windows-1252 → byte original.
   * Solo los 27 code points del rango 0x80-0x9F que Win-1252
   * mapea a posiciones fuera de Latin-1 (C1 control chars).
   */
  private static readonly WIN1252_TO_BYTE = new Map<number, number>([
    [0x20AC, 0x80], // €
    [0x201A, 0x82], // ‚
    [0x0192, 0x83], // ƒ  ← clave para triple enc de vocales acentuadas
    [0x201E, 0x84], // „
    [0x2026, 0x85], // …
    [0x2020, 0x86], // †
    [0x2021, 0x87], // ‡
    [0x02C6, 0x88], // ˆ
    [0x2030, 0x89], // ‰
    [0x0160, 0x8A], // Š
    [0x2039, 0x8B], // ‹
    [0x0152, 0x8C], // Œ
    [0x017D, 0x8E], // Ž
    [0x2018, 0x91], // '
    [0x2019, 0x92], // '
    [0x201C, 0x93], // "  ← clave para triple enc de en-dash/comillas
    [0x201D, 0x94], // "
    [0x2022, 0x95], // •
    [0x2013, 0x96], // –
    [0x2014, 0x97], // —
    [0x02DC, 0x98], // ˜
    [0x2122, 0x99], // ™
    [0x0161, 0x9A], // š
    [0x203A, 0x9B], // ›
    [0x0153, 0x9C], // œ
    [0x017E, 0x9E], // ž
    [0x0178, 0x9F], // Ÿ
  ]);

  constructor() {
    // Pre-ordenar patrones por longitud descendente
    // Usa tiers combinados (62 base + 24 adicionales = 86 total)
    this.tier1Patterns = [...TIER1_HOT_COMBINED].sort(
      (a, b) => b.corrupted.length - a.corrupted.length
    );
    this.tier2Patterns = [...TIER2_WARM_COMBINED].sort(
      (a, b) => b.corrupted.length - a.corrupted.length
    );
    this.tier3Patterns = [...TIER3_COLD_COMBINED].sort(
      (a, b) => b.corrupted.length - a.corrupted.length
    );
    
    this.stats = {
      totalProcessed: 0,
      earlyExits: 0,
      tier1Only: 0,
      tier2Required: 0,
      tier3Required: 0,
      doubleEncodingFixed: 0,
      tripleEncodingFixed: 0,
      averageDurationMs: 0,
    };
  }

  /**
   * Corrige mojibake en texto con sistema de tiers.
   * Early-exit para textos limpios.
   */
  correct(text: string): CorrectionResult {
    const startTime = performance.now();
    this.stats.totalProcessed++;
    
    // Early-exit: texto vacío o null
    if (!text || text.length === 0) {
      this.stats.earlyExits++;
      return this.buildResult(text, text, [], 0, 'EMPTY', startTime);
    }
    
    // Early-exit: ASCII puro (90% de coordenadas)
    if (isCleanASCII(text)) {
      this.stats.earlyExits++;
      return this.buildResult(text, text, [], 0, 'CLEAN_ASCII', startTime);
    }
    
    // Early-exit: no hay indicadores de mojibake
    if (!isSuspicious(text)) {
      this.stats.earlyExits++;
      return this.buildResult(text, text, [], 0, 'NO_MOJIBAKE', startTime);
    }
    
    // Procesamiento completo
    let current = text;
    const tiersApplied: CorrectionResult['tiersApplied'] = [];
    let totalMatches = 0;
    
    // 1. PRE-NORMALIZACIÓN de caracteres variantes (espacios, comillas, etc.)
    // Esto unifica variantes antes de aplicar patrones mojibake
    const charNormalized = normalizeVariantChars(current);
    if (charNormalized !== current) {
      current = charNormalized;
      tiersApplied.push('CHAR_NORMALIZE');
    }
    
    // 2. Intentar corrección de triple encoding (antes de doble)
    if (MOJIBAKE_INDICATORS.TRIPLE_ENCODING.test(current) || this.hasTripleEncodingMarkers(current)) {
      const tripleFixed = this.tryFixTripleEncoding(current);
      if (tripleFixed !== current) {
        current = tripleFixed;
        tiersApplied.push('TRIPLE_ENCODING');
        this.stats.tripleEncodingFixed++;
      }
    }

    // 3. Intentar corrección de doble encoding
    if (MOJIBAKE_INDICATORS.DOUBLE_ENCODING.test(current)) {
      const doubleFixed = this.tryFixDoubleEncoding(current);
      if (doubleFixed !== current) {
        current = doubleFixed;
        tiersApplied.push('DOUBLE_ENCODING');
        this.stats.doubleEncodingFixed++;
      }
    }

    // 4. Aplicar Tier 1 (Hot) - siempre
    const { result: afterTier1, matches: matches1 } = this.applyPatterns(
      current,
      this.tier1Patterns
    );
    if (matches1 > 0) {
      current = afterTier1;
      totalMatches += matches1;
      tiersApplied.push('TIER1');
    }
    
    // 4. Aplicar Tier 2 (Warm) - solo si aún hay mojibake
    if (this.stillHasMojibake(current)) {
      const { result: afterTier2, matches: matches2 } = this.applyPatterns(
        current,
        this.tier2Patterns
      );
      if (matches2 > 0) {
        current = afterTier2;
        totalMatches += matches2;
        tiersApplied.push('TIER2');
        this.stats.tier2Required++;
      }
    }
    
    // 5. Aplicar Tier 3 (Cold) - solo si aún hay mojibake
    if (this.stillHasMojibake(current)) {
      const { result: afterTier3, matches: matches3 } = this.applyPatterns(
        current,
        this.tier3Patterns
      );
      if (matches3 > 0) {
        current = afterTier3;
        totalMatches += matches3;
        tiersApplied.push('TIER3');
        this.stats.tier3Required++;
      }
    }
    
    // 6. Normalización Unicode NFC
    const nfcNormalized = current.normalize('NFC');
    if (nfcNormalized !== current) {
      current = nfcNormalized;
      tiersApplied.push('NFC');
    }
    
    // Actualizar estadísticas
    if (tiersApplied.length === 1 && tiersApplied[0] === 'TIER1') {
      this.stats.tier1Only++;
    }
    
    return this.buildResult(text, current, tiersApplied, totalMatches, undefined, startTime);
  }

  /**
   * Aplica un array de patrones al texto.
   * Usa split().join() para máxima compatibilidad.
   */
  private applyPatterns(
    text: string,
    patterns: MojibakePattern[]
  ): { result: string; matches: number } {
    let result = text;
    let matches = 0;
    
    for (const pattern of patterns) {
      if (result.includes(pattern.corrupted)) {
        const before = result;
        result = result.split(pattern.corrupted).join(pattern.correct);
        if (result !== before) {
          matches++;
        }
      }
    }
    
    return { result, matches };
  }

  /**
   * Verifica si aún quedan indicadores de mojibake.
   */
  private stillHasMojibake(text: string): boolean {
    return (
      MOJIBAKE_INDICATORS.PRIMARY.test(text) ||
      MOJIBAKE_INDICATORS.SECONDARY.test(text) ||
      MOJIBAKE_INDICATORS.CONTROL_CHARS.test(text)
    );
  }

  /**
   * Detecta marcadores de triple encoding: combinación de chars Win-1252
   * del rango 0x80-0x9F (ƒ, ", ", ‚, „, …) junto con Ã o Â.
   * Ejemplo: `ÃƒÂ±` contiene ƒ (0x83 vía Win-1252) + Ã/Â.
   */
  private hasTripleEncodingMarkers(text: string): boolean {
    // Buscar chars Win-1252 del rango 0x80-0x9F que NO existen en Latin-1
    const hasWin1252Char = Array.from(text).some(
      c => EncodingCorrector.WIN1252_TO_BYTE.has(c.codePointAt(0)!)
    );
    // Debe tener también marcadores UTF-8 corruptos (Ã o Â)
    const hasUtf8Markers = /[ÃÂ]/.test(text);
    return hasWin1252Char && hasUtf8Markers;
  }

  /**
   * Corrige triple encoding iterativo (UTF-8 → Latin-1 → Win-1252 → UTF-8 × 3).
   * Convierte chars a bytes respetando el mapa Win-1252 para 0x80-0x9F,
   * luego decodifica como UTF-8. Repite hasta estabilizar (máx 3 pasadas).
   */
  private tryFixTripleEncoding(text: string): string {
    let current = text;
    const MAX_PASSES = 3;

    for (let pass = 0; pass < MAX_PASSES; pass++) {
      try {
        // Convertir chars a bytes usando Win-1252 remapping
        const bytes = new Uint8Array(
          Array.from(current).map(c => {
            const cp = c.codePointAt(0)!;
            // Win-1252 special chars → byte original
            const win1252Byte = EncodingCorrector.WIN1252_TO_BYTE.get(cp);
            if (win1252Byte !== undefined) return win1252Byte;
            // Latin-1 range: codepoint === byte value
            if (cp <= 0xFF) return cp;
            // Fuera de rango: reemplazar con '?' para no romper
            return 0x3F;
          })
        );
        const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
        // Si no cambió, hemos estabilizado
        if (decoded === current) break;
        current = decoded;
      } catch {
        // Fallo en decodificación UTF-8: retornar último estado válido
        break;
      }
    }
    return current;
  }

  /**
   * Intenta corregir doble encoding (UTF-8 → Latin-1 → UTF-8).
   * Usa TextDecoder nativo del browser.
   */
  private tryFixDoubleEncoding(text: string): string {
    try {
      // Convertir a bytes como si fuera Latin-1
      const bytes = new Uint8Array(text.split('').map(c => c.charCodeAt(0)));
      // Decodificar como UTF-8
      const decoded = new TextDecoder('utf-8', { fatal: true }).decode(bytes);
      return decoded;
    } catch {
      // Si falla, retornar original
      return text;
    }
  }

  /**
   * Construye el resultado de corrección.
   */
  private buildResult(
    original: string,
    corrected: string,
    tiersApplied: CorrectionResult['tiersApplied'],
    patternsMatched: number,
    earlyExitReason: CorrectionResult['earlyExitReason'],
    startTime: number
  ): CorrectionResult {
    const durationMs = performance.now() - startTime;
    
    // Actualizar promedio de duración
    const n = this.stats.totalProcessed;
    this.stats.averageDurationMs =
      (this.stats.averageDurationMs * (n - 1) + durationMs) / n;
    
    return {
      corrected,
      wasModified: original !== corrected,
      tiersApplied,
      patternsMatched,
      earlyExitReason,
      durationMs,
    };
  }

  /**
   * Obtiene estadísticas de uso.
   */
  getStats(): CorrectionStats {
    return { ...this.stats };
  }

  /**
   * Resetea estadísticas.
   */
  resetStats(): void {
    this.stats = {
      totalProcessed: 0,
      earlyExits: 0,
      tier1Only: 0,
      tier2Required: 0,
      tier3Required: 0,
      doubleEncodingFixed: 0,
      tripleEncodingFixed: 0,
      averageDurationMs: 0,
    };
  }
}

// ============================================================================
// INSTANCIA GLOBAL (SINGLETON)
// ============================================================================

/**
 * Instancia global de EncodingCorrector para uso en toda la aplicación.
 * Reutiliza patrones pre-ordenados para máximo rendimiento.
 */
export const encodingCorrector = new EncodingCorrector();

/**
 * Función de conveniencia para corrección rápida.
 * Usa la instancia global.
 */
export function correctEncoding(text: string): CorrectionResult {
  return encodingCorrector.correct(text);
}

/**
 * Función simplificada que solo retorna el texto corregido.
 */
export function fixMojibake(text: string): string {
  return encodingCorrector.correct(text).corrected;
}
